import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Star, Plus, Trash2, Save, CheckCircle2, AlertCircle, Info, ArrowRight } from "lucide-react";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { kritischeAenderungBestaetigen } from "../utils/veranstaltungstag";

export default function AgeBonus() {
  const [ageBonuses, setAgeBonuses] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState(null);
  // Feldweise Fehler (leere Felder) erst nach einem Speicherversuch rot machen –
  // eine frisch hinzugefuegte, noch leere Zeile soll nicht sofort meckern.
  // Bereichs- und Ueberschneidungsfehler werden dagegen sofort angezeigt.
  const [saveAttempted, setSaveAttempted] = useState(false);
  // Stand beim Laden bzw. nach dem letzten Speichern – damit erkennen wir
  // ungespeicherte Aenderungen. Wichtig fuer den Fall "alle Regeln geloescht":
  // dann ist die Liste leer, aber es gibt sehr wohl etwas zu speichern.
  const [loadedSnapshot, setLoadedSnapshot] = useState("[]");

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();

  const dirty = JSON.stringify(ageBonuses) !== loadedSnapshot;

  // Spiegelt functions/utils/validateAgeBonusRules.js – dieselben Regeln, damit
  // Frontend und Backend nie auseinanderlaufen.
  const pruefung = useMemo(() => berechnePruefung(ageBonuses), [ageBonuses]);

  const loadAgeBonuses = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchWithAuth(`${BACKEND_URL}/age-bonus`, navigate);
      const liste = Array.isArray(data) ? data : [];
      setAgeBonuses(liste);
      setLoadedSnapshot(JSON.stringify(liste));
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: `Ladefehler: ${err.message}` });
    } finally {
      setIsLoading(false);
    }
  }, [BACKEND_URL, navigate]);

  useEffect(() => {
    loadAgeBonuses();
  }, [loadAgeBonuses]);

  const handleChange = (index, field, value) => {
    const updated = [...ageBonuses];
    updated[index][field] = value === "" ? null : Number(value);
    setAgeBonuses(updated);
  };

  const handleSave = async () => {
    // Ungueltige Regeln gar nicht erst abschicken – das Backend wuerde sie mit
    // 400 ablehnen, aber der Admin soll sofort sehen, was zu korrigieren ist.
    if (pruefung.hasBlocking) {
      setSaveAttempted(true);
      setMessage({
        type: "error",
        text: "Bitte korrigiere die rot markierten Regeln, bevor du speicherst.",
      });
      return;
    }

    if (!(await kritischeAenderungBestaetigen(
      "Altersbonus ändern – die Gesamtpunkte der Kinderteams werden neu berechnet."
    ))) return;

    setIsSaving(true);
    setMessage(null);
    try {
      await fetchWithAuth(`${BACKEND_URL}/age-bonus`, navigate, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(ageBonuses),
      });

      setLoadedSnapshot(JSON.stringify(ageBonuses));
      setSaveAttempted(false);
      setMessage({ type: "success", text: "Änderungen erfolgreich gespeichert!" });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: `Speicherfehler: ${err.message}` });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddRow = () => {
    setAgeBonuses([...ageBonuses, { min_age: null, max_age: null, bonus_points: null }]);
  };

  const handleDeleteRow = (index) => {
    setAgeBonuses(ageBonuses.filter((_, i) => i !== index));
  };

  const handleFocus = (e) => {
    e.target.select();
  };

  return (
    <div className="p-4 sm:p-6 bg-secondary-950 min-h-screen">
      <div className="max-w-3xl mx-auto">
        {/* Kopfbereich mit Titel + Erklaerung */}
        <header className="mb-6">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
              <Star size={22} />
            </span>
            <div>
              <h1 className="text-2xl font-bold text-white leading-tight">Altersbonus</h1>
              <p className="text-sm text-secondary-100">Bonus-Punkte für Kinderteams je nach Durchschnittsalter</p>
            </div>
          </div>
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-secondary-800 bg-secondary-900 p-3 text-sm text-secondary-100">
            <Info size={16} className="mt-0.5 shrink-0 text-primary-500" />
            <p>
              Liegt das Durchschnittsalter eines Kinderteams in einem der Bereiche, werden die
              zugehörigen Bonus-Punkte auf seine Gesamtwertung addiert. So gleichen jüngere
              Teams ihren Nachteil gegenüber älteren aus. Das Durchschnittsalter wird auf ganze
              Jahre abgerundet (8,5 zählt als 8). Den Bonus gibt es erst, sobald für das Team
              mindestens ein Ergebnis eingetragen ist.
            </p>
          </div>
        </header>

        {isLoading ? (
          <div className="py-16 text-center text-secondary-200">Lade Daten…</div>
        ) : (
          <>
            {ageBonuses.length === 0 ? (
              /* Leerzustand */
              <div className="rounded-xl border border-dashed border-secondary-700 bg-secondary-900/50 py-12 px-6 text-center">
                <Star size={28} className="mx-auto mb-3 text-secondary-500" />
                <p className="text-white font-medium">Noch keine Altersregeln</p>
                <p className="mt-1 text-sm text-secondary-200">
                  Lege deine erste Regel an, um Kinderteams einen Altersbonus zu geben.
                </p>
                <button
                  onClick={handleAddRow}
                  className="mt-5 inline-flex items-center gap-2 rounded-lg bg-primary-500 px-5 py-2.5 font-semibold text-secondary-950 shadow transition-colors hover:bg-primary-600"
                >
                  <Plus size={18} /> Erste Regel hinzufügen
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {ageBonuses.map((row, idx) => {
                  const p = pruefung.rows[idx];
                  const rangeInvalid = p.rangeBad;
                  const overlapInvalid = p.overlaps.length > 0;
                  // Alterfelder rot bei leerem Feld (nach Speicherversuch),
                  // ungueltigem Bereich oder Ueberschneidung.
                  const minInvalid = (saveAttempted && p.minBad) || rangeInvalid || overlapInvalid;
                  const maxInvalid = (saveAttempted && p.maxBad) || rangeInvalid || overlapInvalid;
                  const bonusInvalid = saveAttempted && p.bonusBad;
                  const missingAfterSave =
                    saveAttempted && (p.minBad || p.maxBad || p.bonusBad);

                  return (
                    <div
                      key={idx}
                      className="rounded-xl border border-secondary-800 bg-secondary-900 p-4 shadow-lg transition-colors hover:border-secondary-700"
                    >
                      <div className="flex items-center justify-between">
                        <span className="badge badge-secondary">Regel {idx + 1}</span>
                        <button
                          onClick={() => handleDeleteRow(idx)}
                          aria-label={`Regel ${idx + 1} löschen`}
                          title="Regel löschen"
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-secondary-200 transition-colors hover:bg-red-500/15 hover:text-red-400"
                        >
                          <Trash2 size={17} />
                        </button>
                      </div>

                      {/* Regel als lesbare Zeile: Alter von X bis Y  ->  +Z Punkte */}
                      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
                        <Field
                          label="Alter von"
                          unit="Jahre"
                          value={row.min_age}
                          onChange={(v) => handleChange(idx, "min_age", v)}
                          onFocus={handleFocus}
                          invalid={minInvalid}
                        />
                        <Field
                          label="bis"
                          unit="Jahre"
                          value={row.max_age}
                          onChange={(v) => handleChange(idx, "max_age", v)}
                          onFocus={handleFocus}
                          invalid={maxInvalid}
                        />

                        <ArrowRight
                          size={20}
                          className="hidden shrink-0 text-secondary-400 sm:mb-2.5 sm:block"
                        />

                        <Field
                          label="Bonus"
                          unit="Punkte"
                          value={row.bonus_points}
                          onChange={(v) => handleChange(idx, "bonus_points", v)}
                          onFocus={handleFocus}
                          accent
                          invalid={bonusInvalid}
                        />
                      </div>

                      {(rangeInvalid || overlapInvalid || missingAfterSave) && (
                        <div className="mt-2 space-y-1 text-xs text-red-400">
                          {rangeInvalid && (
                            <p className="flex items-center gap-1.5">
                              <AlertCircle size={13} className="shrink-0" />
                              Das Mindestalter ist größer als das Höchstalter.
                            </p>
                          )}
                          {overlapInvalid && (
                            <p className="flex items-center gap-1.5">
                              <AlertCircle size={13} className="shrink-0" />
                              Überschneidet sich mit Regel {p.overlaps.join(", ")}.
                            </p>
                          )}
                          {missingAfterSave && !rangeInvalid && (
                            <p className="flex items-center gap-1.5">
                              <AlertCircle size={13} className="shrink-0" />
                              Bitte alle Felder ausfüllen.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                <button
                  onClick={handleAddRow}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-secondary-700 py-3 font-medium text-secondary-100 transition-colors hover:border-primary-500 hover:text-primary-500"
                >
                  <Plus size={18} /> Zeile hinzufügen
                </button>
              </div>
            )}

            {/* Speicherleiste – auch bei leerer Liste sichtbar, sobald es
                ungespeicherte Aenderungen gibt (z. B. alle Regeln geloescht). */}
            {(ageBonuses.length > 0 || dirty) && (
              <div className="sticky bottom-0 mt-6 -mx-4 border-t border-secondary-800 bg-secondary-950/95 px-4 py-4 backdrop-blur sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:p-0">
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className={`flex w-full items-center justify-center gap-2 rounded-lg px-6 py-3 font-semibold shadow transition-colors sm:w-auto ${
                    isSaving
                      ? "cursor-not-allowed bg-secondary-700 text-secondary-200"
                      : "bg-primary-500 text-secondary-950 hover:bg-primary-600"
                  }`}
                >
                  <Save size={18} />
                  {isSaving ? "Speichern…" : "Speichern"}
                </button>
              </div>
            )}

            {message && (
              <div
                className={`mt-4 flex items-center gap-2 rounded-lg p-4 font-medium ${
                  message.type === "success"
                    ? "bg-green-500/15 text-green-300"
                    : "bg-red-500/15 text-red-300"
                }`}
              >
                {message.type === "success" ? (
                  <CheckCircle2 size={18} className="shrink-0" />
                ) : (
                  <AlertCircle size={18} className="shrink-0" />
                )}
                {message.text}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Prueft die Regeln fuer die Oberflaeche. Muss inhaltlich mit
// functions/utils/validateAgeBonusRules.js uebereinstimmen. Liefert pro Regel
// die fehlerhaften Felder plus die Nummern der Regeln, mit denen sie sich
// ueberschneidet, sowie ein Gesamt-Flag, das das Speichern blockiert.
function berechnePruefung(rules) {
  const istGanzzahlAbNull = (w) =>
    typeof w === "number" && Number.isInteger(w) && w >= 0;
  const istZahlAbNull = (w) =>
    typeof w === "number" && Number.isFinite(w) && w >= 0;

  const rows = rules.map((rg) => {
    const minBad = !istGanzzahlAbNull(rg.min_age);
    const maxBad = !istGanzzahlAbNull(rg.max_age);
    const bonusBad = !istZahlAbNull(rg.bonus_points);
    const rangeBad = !minBad && !maxBad && rg.min_age > rg.max_age;
    return { minBad, maxBad, bonusBad, rangeBad, overlaps: [] };
  });

  // Ueberschneidungen nur zwischen Regeln mit fuer sich gueltigem Bereich.
  // Beide Grenzen inklusiv -> geteilte Grenze (6-8 / 8-11) ueberlappt ebenfalls.
  const gueltig = rules
    .map((rg, i) => ({ rg, i }))
    .filter(
      ({ rg }) =>
        istGanzzahlAbNull(rg.min_age) &&
        istGanzzahlAbNull(rg.max_age) &&
        rg.min_age <= rg.max_age
    );

  for (let a = 0; a < gueltig.length; a++) {
    for (let b = a + 1; b < gueltig.length; b++) {
      const A = gueltig[a];
      const B = gueltig[b];
      if (A.rg.min_age <= B.rg.max_age && B.rg.min_age <= A.rg.max_age) {
        rows[A.i].overlaps.push(B.i + 1);
        rows[B.i].overlaps.push(A.i + 1);
      }
    }
  }

  const hasBlocking = rows.some(
    (r) => r.minBad || r.maxBad || r.bonusBad || r.rangeBad || r.overlaps.length > 0
  );

  return { rows, hasBlocking };
}

// Beschriftetes Zahlenfeld mit Einheit – ersetzt die nackten w-20-Inputs.
function Field({ label, unit, value, onChange, onFocus, accent, invalid }) {
  return (
    <label className="flex flex-1 flex-col gap-1 sm:min-w-[7rem]">
      <span className="text-xs font-medium text-secondary-200">{label}</span>
      <span
        className={`flex items-center rounded-lg border bg-secondary-800 px-3 transition-colors focus-within:ring-2 ${
          invalid
            ? "border-red-500/60 focus-within:ring-red-500/40"
            : accent
              ? "border-primary-500/40 focus-within:border-primary-500 focus-within:ring-primary-500/40"
              : "border-secondary-700 focus-within:border-primary-500 focus-within:ring-primary-500/40"
        }`}
      >
        <input
          type="number"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          onFocus={onFocus}
          placeholder="–"
          className={`w-full bg-transparent py-2 text-white outline-none placeholder:text-secondary-500 ${
            accent ? "font-semibold" : ""
          }`}
        />
        <span className="ml-2 shrink-0 text-xs text-secondary-300">{unit}</span>
      </span>
    </label>
  );
}
